import { observable, action, computed, makeObservable } from 'mobx'
import { createIntlCache, createIntl } from 'react-intl'
import get from 'lodash/get'
import moment from 'moment-timezone'

import { NULL as BlockModelNULL } from '@client/components/Block/model'
import localeData from '@client/i18n/localeData'
import { languages } from '@client/i18n/constants'
import History from '@client/utils/History'
import { captureException } from '@client/utils/sentry'
import { VIEW_TYPES, HISTORY_LIMIT } from '@client/components/constants'
import {
  QUERY_OPTIONS,
  QUERY_OPTIONS_TYPE_EDITOR,
} from '@client/components/FileManager/constants'

const locales = languages.map((lang) => lang.locale)

const cache = createIntlCache()

const DEFAULT_WIZARD_STEP = 0

/**
 * Model of App component
 */
class AppModel {
  constructor(history) {
    makeObservable(this)
    this.history = history || new History(HISTORY_LIMIT)
  }

  @observable draggedComponent = BlockModelNULL
  @action setDraggedComponent = (model) => {
    this.draggedComponent = model
  }

  @observable editingSettings = BlockModelNULL
  @action setEditingSettings = (obj) => {
    this.editingSettings = obj
  }

  @observable locale = 'en'
  @action setLocale = (lang) => {
    this.locale = locales.includes(lang) ? lang : 'en'
  }

  @observable timezone = null
  @action setTimezone = (timezone) => {
    this.timezone = timezone
  }

  @computed get intl() {
    const messages = localeData[this.locale]
    const intl = createIntl(
      {
        locale: this.locale,
        messages: messages,
      },
      cache
    )
    return intl
  }

  @observable filemanagerQueryOptions = QUERY_OPTIONS[QUERY_OPTIONS_TYPE_EDITOR]
  @observable view = VIEW_TYPES.EDITOR
  @action setView = (
    val,
    filemanagerQueryOptions = QUERY_OPTIONS[QUERY_OPTIONS_TYPE_EDITOR]
  ) => {
    this.view = val
    this.filemanagerQueryOptions = filemanagerQueryOptions
  }

  @observable user = {}
  @action setUser = (user) => {
    this.user = user
  }

  @observable profile = {}
  // `language` is the personal UI language from v4/user, which callers should
  // always pass when available — v3/profile.language was repurposed into the
  // account/billing language (FE-1175/FE-1176) and no longer reflects it. See
  // FE-1259. Falls back to profile.language so callers that can't reach
  // v4/user (or whose fetch failed) keep the pre-FE-1259 behavior.
  @action setProfile = (profile, language) => {
    this.profile = profile

    this.setUserFeatureFlags(get(profile, 'feature_flags') || {})
    this.setUserFeatures(get(profile, 'features') || {})
    this.setSubscription(get(profile, 'subscription') || {})
    this.setTrial(get(profile, 'trial') || {})
    this.setPendingDowngrade(
      get(profile, 'subscription.pendingDowngrade') || {}
    )

    this.setLocale(language ?? get(profile, 'language'))
    this.setTimezone(get(profile, 'timezone'))
  }

  @observable userFeatures = {}
  @action setUserFeatures = (userFeatures) => {
    this.userFeatures = userFeatures
  }

  @observable userFeatureFlags = {}
  @action setUserFeatureFlags = (userFeatureFlags) => {
    this.userFeatureFlags = userFeatureFlags
  }

  @observable subscription = {}
  @action setSubscription = (subscription) => {
    this.subscription = subscription
  }

  @observable trial = {}
  @action setTrial = (trial) => {
    this.trial = trial
  }

  @observable pendingDowngrade = {}
  @action setPendingDowngrade = (pendingDowngrade) => {
    this.pendingDowngrade = pendingDowngrade
  }

  @observable stepIndex = DEFAULT_WIZARD_STEP
  @action setStepIndex = (stepIndex) => {
    this.stepIndex = stepIndex
  }

  @observable wizard = {}
  @action setWizard = (wizard) => {
    this.wizard = wizard
  }

  // Sanitized in-app path to return to on save & close, passed by the launching
  // app (frontend-v3) via the `next` query param (see utils/returnUrl). Empty
  // when absent/unsafe, in which case getNextUrl falls back to its hardcoded
  // path.
  //
  // Keyed to the wizard hash AND type it was captured for. The wizard route
  // actions re-run setReturnUrl on every entry — including when the user steps
  // back to an earlier wizard step via the breadcrumb, which links back with no
  // query. In that case we must KEEP the value (same wizard session), whereas
  // entering a *different* wizard (different hash, or same hash but a different
  // type — hash uniqueness is only guaranteed within a single item type, see
  // FE-1216) without `next` must clear the stale one.
  @observable returnUrl = ''
  @observable returnUrlHash = ''
  @observable returnUrlType = ''
  @action setReturnUrl = (returnUrl, hash = '', type = '') => {
    if (returnUrl) {
      this.returnUrl = returnUrl
      this.returnUrlHash = hash
      this.returnUrlType = type
    } else if (hash !== this.returnUrlHash || type !== this.returnUrlType) {
      // different wizard (or none) and no new `next` → drop any stale value
      this.returnUrl = ''
      this.returnUrlHash = ''
      this.returnUrlType = ''
    }
    // else: same wizard, no new `next` (e.g. breadcrumb back to an earlier
    // step) → keep
  }

  @computed get hasWizard() {
    return !!get(this.wizard, 'hash')
  }

  @computed get isOnTrial() {
    const start = get(this.trial, 'start_date')
    const end = get(this.trial, 'end_date')
    const upgraded = get(this.trial, 'upgrade_date')
    if (!start || !end || !!upgraded) {
      return false
    }
    const now = moment()
    const isAfterStart = !!start && now.isAfter(start)
    const isBeforeEnd = !!end && now.isBefore(end)

    return isAfterStart && isBeforeEnd
  }

  @computed get usedTrial() {
    return !!get(this.trial, 'start_date')
  }

  @computed get trialDaysLeft() {
    if (!get(this.trial, 'end_date')) {
      return 0
    }
    return Math.abs(moment(this.trial.end_date).diff(moment(), 'days')) + 1
  }

  @computed get isOnStartPlan() {
    return get(this.subscription, 'plan_level') === 10
  }

  @computed get isOnStandardPlan() {
    return get(this.subscription, 'plan_level') === 20
  }

  /**
   * Provides string formatting
   * https://github.com/yahoo/react-intl/wiki/API#formatjs-internationalization-formatters
   * https://github.com/yahoo/react-intl/wiki/Components#formattedmessage
   *
   * @param id
   * @param values
   * @return {string}
   */
  formatMessage = (id, values) => {
    // `@formatjs/intl` throws an invariant when `id` is falsy. Server-side the
    // render runs inside an `fs.readFile` callback, so that throw escapes
    // Express, reaches Node's default uncaught-exception handler and exits the
    // whole renderer process — killing every other in-flight render with it
    // (FE-1348). Callers routinely pass ids looked up from stored block data
    // (`SOCIAL_SERVICE_NAMES[item.name]` and friends), which can legitimately
    // miss when that data predates a rename or removal, so a missing id has to
    // degrade to empty text rather than take the service down.
    if (!id) {
      const error = new Error(
        `formatMessage called without a message id (received ${typeof id})`
      )
      // Logged as well as captured: the console line is what shows up in the
      // renderer pod's own logs, which is where this gets diagnosed from.
      console.error(error)
      captureException(error)
      return ''
    }
    return this.intl.formatMessage({ id }, values)
  }

  /**
   * Provide date formatting
   * https://github.com/yahoo/react-intl/wiki/API
   * https://github.com/yahoo/react-intl/wiki/Components
   *
   * @param date
   * @param opts
   * @return {*}
   */
  formatDate = (date, opts) => {
    return this.intl.formatDate(date, opts)
  }
}

export const NULL = new AppModel()

export default AppModel
